import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useAuth } from "./AuthContext";
import {
  fetchContacts,
  fetchContactDetail,
  attachTag as apiAttachTag,
  detachTag as apiDetachTag,
  createContact as apiCreateContact,
  createCustomerType as apiCreateCustomerType,
  createContactMethod as apiCreateContactMethod,
  getAllCustomerTypes,
  getAllContactMethods,
  getAllOwners,
} from "../api/contacts";

const ContactsContext = createContext(null);

export function ContactsProvider({ children }) {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [pageSize, setPageSize] = useState(50);
  
  // Store current filters so refreshContacts can use them
  const [currentFilters, setCurrentFilters] = useState({});

  // Auswahl (nur ID)
  const [selectedContactId, setSelectedContactId] = useState(null);

  // Detaildaten
  const [contactDetails, setContactDetails] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);

  // Options für Dropdowns
  const [owners, setOwners] = useState([]);
  const [contactMethods, setContactMethods] = useState([]);
  const [contactType, setContactType] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // Options erst laden, wenn ein User eingeloggt ist
  useEffect(() => {
    const loadOptions = async () => {
      try {
        setOptionsLoading(true);
        const [typesRes, methodsRes, ownersRes] = await Promise.all([
          getAllCustomerTypes(),
          getAllContactMethods(),
          getAllOwners(),
        ]);
        
        // OptionsResponse format: { items: [{id, label, type, method, username}] }
        // Behalte ALLE Felder, nicht nur id+label
        setContactType(typesRes.items || []);
        setContactMethods(methodsRes.items || []);
        setOwners(ownersRes.items || []);
      } catch (err) {
        // Silent fail
      } finally {
        setOptionsLoading(false);
      }
    };

    if (!user) {
      setContactType([]);
      setContactMethods([]);
      setOwners([]);
      setOptionsLoading(false);
      return;
    }

    loadOptions();
  }, [user]);

  const hasActiveFilters = (filters = {}) => {
    return (
      (filters.hasEmail && filters.hasEmail !== "both") ||
      (filters.hasPhone && filters.hasPhone !== "both") ||
      (filters.hasEvent && filters.hasEvent !== "both") ||
      (filters.canContact && filters.canContact !== "both") ||
      (filters.customerTypes && filters.customerTypes.length > 0) ||
      (filters.contactMethods && filters.contactMethods.length > 0) ||
      (filters.contactOwners && filters.contactOwners.length > 0) ||
      (filters.tags && filters.tags.length > 0)
    );
  };

  // Liste neu laden
  const refreshContacts = useCallback(async ({ q = "", filters = {} } = {}) => {
    setLoading(true);
    setCurrentFilters(filters);
    setNextCursor(null);
    setHasMore(false);
    setPageSize(50);
    try {
      const page = await fetchContacts({ limit: 50, q, filters });
      setContacts(page.items);
      setNextCursor(page.nextCursor);
      setHasMore(!!page.nextCursor);
    } catch (err) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, []);

  // Weitere laden
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    try {
      if (hasActiveFilters(currentFilters)) {
        const nextSize = pageSize + 50;
        const page = await fetchContacts({ limit: nextSize, filters: currentFilters });
        const items = Array.isArray(page.items) ? page.items : [];
        // Wenn keine neuen Items mehr kommen, stoppe weitere Aufrufe
        if (items.length <= contacts.length) {
          setHasMore(false);
          setNextCursor(null);
          return;
        }
        setContacts(items);
        setNextCursor(page.nextCursor);
        setHasMore(!!page.nextCursor);
        setPageSize(nextSize);
      } else {
        const page = await fetchContacts({ limit: 50, cursor: nextCursor, filters: currentFilters });
        const items = Array.isArray(page.items) ? page.items : [];
        if (items.length === 0) {
          setHasMore(false);
          setNextCursor(null);
          return;
        }
        setContacts((prev) => [...prev, ...items]);
        setNextCursor(page.nextCursor);
        setHasMore(!!page.nextCursor);
      }
    } catch (err) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, nextCursor, currentFilters, pageSize, contacts]);

  // Detail immer neu von der API holen
  const getContactDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const detail = await fetchContactDetail(id);
      setContactDetails((prev) => ({ ...prev, [id]: detail }));
      return detail;
    } catch (err) {
      throw err;
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Tags: anhängen / entfernen (über Contacts-API)
  const addTagToContact = useCallback(async (contactId, tagId, body = null) => {
    await apiAttachTag(contactId, tagId, body); // 204 No Content erwartet
  }, []);

  const removeTagFromContact = useCallback(async (contactId, tagId) => {
    await apiDetachTag(contactId, tagId); // 204 No Content erwartet
  }, []);

  // Neuen Kontakt erstellen
  const createContact = useCallback(async (contactData) => {
    const result = await apiCreateContact(contactData);
    await refreshContacts(); // Liste neu laden
    return result;
  }, [refreshContacts]);

  // Neue Customer Type hinzufügen
  const addCustomerType = useCallback(async (name) => {
    const newType = await apiCreateCustomerType(name);
    setContactType((prev) => [...prev, { id: newType.id, name: newType.label }]);
    return newType;
  }, []);

  // Neue Contact Method hinzufügen
  const addContactMethod = useCallback(async (name) => {
    const newMethod = await apiCreateContactMethod(name);
    setContactMethods((prev) => [...prev, { id: newMethod.id, name: newMethod.label }]);
    return newMethod;
  }, []);

  return (
    <ContactsContext.Provider
      value={{
        contacts,
        loading,
        hasMore,
        refreshContacts,
        loadMore,
        // Auswahl
        selectedContactId,
        setSelectedContactId,
        // Details
        contactDetails,
        detailLoading,
        getContactDetail,
        // Tags
        addTagToContact,
        removeTagFromContact,
        // Options
        owners,
        contactMethods,
        contactType,
        optionsLoading,
        // Create
        createContact,
        addCustomerType,
        addContactMethod,
      }}
    >
      {children}
    </ContactsContext.Provider>
  );
}

export function useContacts() {
  return useContext(ContactsContext);
}
